import { Draft } from 'immer';

import { finleyAssert } from '@finley-technologies/common';

import { immer } from 'zustand/middleware/immer';
import { createWithEqualityFn } from 'zustand/traditional';
import { CreditAgreementFullArtifact } from '~/core/routes/settings/implementations/common/types';

export type CreditAgreementStore = {
  isSaving: boolean;
  isCreditAgreementDirty: boolean;
  lastSaved: number;
  creditAgreement: CreditAgreementFullArtifact | null;
  previousCreditAgreement: CreditAgreementFullArtifact | null;
  isEditing: boolean;
  showOnlyChangesInNav: boolean;

  setShowOnlyChangesInNav: (showOnlyChangesInNav: boolean) => void;
  setIsEditing: (isEditing: boolean) => void;
  updateCreditAgreement: (
    fn: (draft: Draft<CreditAgreementFullArtifact>) => void,
  ) => void;
  setPreviousCreditAgreement: (
    creditAgreement: CreditAgreementFullArtifact,
  ) => void;
};

// These actions are used only inside the CreditAgreementProvider, so they
// are hidden from the publicly exposed type.
type CreditAgreementStatePrivateActions = {
  setCreditAgreementDirty: () => void;
  setSaveStart: () => void;
  setSaveComplete: () => void;
  setCreditAgreement: (creditAgreement: CreditAgreementFullArtifact) => void;
};

type CreditAgreementPrivateStore = CreditAgreementStore &
  CreditAgreementStatePrivateActions;

export const CreateUseCreditAgreementStore = () =>
  createWithEqualityFn<CreditAgreementPrivateStore>()(
    immer((set) => ({
      isSaving: false,
      isCreditAgreementDirty: false,
      lastSaved: 0,
      creditAgreement: null,
      previousCreditAgreement: null,
      isEditing: true,
      showOnlyChangesInNav: true,

      setShowOnlyChangesInNav: (showOnlyChangesInNav) =>
        set({ showOnlyChangesInNav }),
      setIsEditing: (isEditing) => set({ isEditing }),
      setCreditAgreementDirty: () => set({ isCreditAgreementDirty: true }),
      setSaveStart: () => set({ isSaving: true }),
      setSaveComplete: () =>
        set({
          isSaving: false,
          isCreditAgreementDirty: false,
          lastSaved: Date.now(),
        }),
      setCreditAgreement: (creditAgreement) => set({ creditAgreement }),
      setPreviousCreditAgreement: (previousCreditAgreement) =>
        set({ previousCreditAgreement }),
      updateCreditAgreement: (fn) =>
        set((draft) => {
          finleyAssert(
            draft.creditAgreement,
            'The creditAgreement has not yet been loaded into the zustand store',
          );

          fn(draft.creditAgreement);
        }),
    })),
  );

export type UseCreditAgreementStore = ReturnType<
  typeof CreateUseCreditAgreementStore
>;
